import { getQuestionnaireAnswers } from "@medplum/core";
import { Encounter, QuestionnaireResponse } from "@medplum/fhirtypes";

import _ from "lodash";

import { usDateFormat } from "@/utils/datetime";
import { getQuestionnaireResponseAnswersSimplified } from "@/utils/medplum";

import {
  EncounterAmendmentReason,
  EncounterAmendmentReasonLabels,
} from "../constants/encounter-amendment-reason";
import { NewbornPostpartumStateOfNewbornQuestionsLinkIds } from "../constants/newborn-postpartum";

/**
 * Sort encounters by most recent to less recent date
 * and format period.start to MM/DD/YYYY
 *
 * @param encounters
 * @returns A list of Encounters
 */
export function sortEncountersPerDate(encounters: Encounter[]): Encounter[] {
  const sortedEncounters = _(encounters)
    .sortBy((e) => e.period?.start)
    .reverse()
    .map((e) => ({
      ...e,
      period: {
        ...e.period,
        start: e.period?.start ? usDateFormat(e.period.start) : "",
      },
    }))
    .value();

  return sortedEncounters;
}

/**
 * Get the amendment reason from the questionnaire response
 * @param questionnaireResponse
 * @returns The amendment reason
 */
export function getAmendmentReason(questionnaireResponse: QuestionnaireResponse) {
  const answers = getQuestionnaireAnswers(questionnaireResponse);
  if (!answers) return null;

  const amendmentReason = answers["encounterAmendmentInfoReason"]?.valueString;

  if (!amendmentReason) return null;

  if (amendmentReason === EncounterAmendmentReason.Other) {
    return `${EncounterAmendmentReasonLabels[amendmentReason as EncounterAmendmentReason]} - ${answers["encounterAmendmentInfoSpecify"]?.valueString ?? ""}`;
  }

  return EncounterAmendmentReasonLabels[amendmentReason as EncounterAmendmentReason];
}

export const getStateOfNewbornAnswersVersions = (
  previousVersion: QuestionnaireResponse,
  currentVersion: QuestionnaireResponse,
) => {
  const previousAnswers = getQuestionnaireResponseAnswersSimplified(previousVersion);
  const currentAnswers = getQuestionnaireResponseAnswersSimplified(currentVersion);

  const previousName =
    previousAnswers[NewbornPostpartumStateOfNewbornQuestionsLinkIds.NewbornName]?.value[0] || "";
  const currentName =
    currentAnswers[NewbornPostpartumStateOfNewbornQuestionsLinkIds.NewbornName]?.value[0] || "";
  const previousStateOfNewborn =
    previousAnswers[NewbornPostpartumStateOfNewbornQuestionsLinkIds.NewbornState]?.value?.join(
      ", ",
    ) || "";
  const currentStateOfNewborn =
    currentAnswers[NewbornPostpartumStateOfNewbornQuestionsLinkIds.NewbornState]?.value?.join(
      ", ",
    ) || "";
  const previousSex =
    previousAnswers[NewbornPostpartumStateOfNewbornQuestionsLinkIds.Sex]?.value[0] || "";
  const currentSex =
    currentAnswers[NewbornPostpartumStateOfNewbornQuestionsLinkIds.Sex]?.value[0] || "";
  const previousSexSpecify =
    previousAnswers[NewbornPostpartumStateOfNewbornQuestionsLinkIds.SexSpecify]?.value[0] || "";
  const currentSexSpecify =
    currentAnswers[NewbornPostpartumStateOfNewbornQuestionsLinkIds.SexSpecify]?.value[0] || "";

  const previousSexDisplay = previousSex === "Other" ? previousSexSpecify : previousSex;
  const currentSexDisplay = currentSex === "Other" ? currentSexSpecify : currentSex;

  return {
    previousName,
    currentName,
    previousStateOfNewborn,
    currentStateOfNewborn,
    previousSexDisplay,
    currentSexDisplay,
  };
};
